import React from 'react';
import { BarChart2, Trash2, Layers, Copy } from 'lucide-react';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-teal-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты расчетов для сравнения.
        </p>
      </div>
    );
  }

  // Найти названия материалов
  const getMaterialName = (materialType) => {
    const materialNames = {
      'pla': 'PLA',
      'abs': 'ABS',
      'petg': 'PETG',
      'nylon': 'Нейлон',
      'tpu': 'TPU (Гибкий)',
      'resin': 'Фотополимер',
      'nylon_powder': 'Нейлоновый порошок',
      'metal_powder': 'Металлический порошок'
    };
    
    return materialNames[materialType] || materialType;
  };

  // Найти названия принтеров
  const getPrinterName = (printerType) => {
    const printerNames = {
      'fdm': 'FDM',
      'sla': 'SLA',
      'sls': 'SLS',
      'slm': 'SLM',
      'polyjet': 'PolyJet'
    };
    
    return printerNames[printerType] || printerType;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-teal-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'вариант' : savedCalculations.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <Layers className="w-4 h-4 mr-1.5 text-teal-500" />
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Copy className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Материал:</span>
                <span className="font-medium">
                  {getMaterialName(calc.formData.materialType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Технология:</span>
                <span>
                  {getPrinterName(calc.formData.printerType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Объем:</span>
                <span>
                  {calc.formData.modelVolume} см³
                </span>
              </div>
              <div className="flex justify-between">
                <span>Цена (1 шт.):</span>
                <span className="font-semibold text-teal-600 dark:text-teal-400">
                  {formatNumber(calc.results.sellingPriceUnit, 2)} ₽
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {savedCalculations.length >= 2 && (
        <div className="mt-6 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Сравнительная таблица
          </h4>
          <div className="overflow-x-auto max-h-[200px] custom-scrollbar">
            <table className="w-full text-xs text-gray-700 dark:text-gray-300">
              <thead className="bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                <tr>
                  <th className="p-2 text-left">Материал</th>
                  <th className="p-2 text-left">Объем</th>
                  <th className="p-2 text-right">Кол-во</th>
                  <th className="p-2 text-right">Цена/шт</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {savedCalculations.map(calc => (
                  <tr key={calc.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="p-2">{getMaterialName(calc.formData.materialType)}</td>
                    <td className="p-2">{calc.formData.modelVolume} см³</td>
                    <td className="p-2 text-right">{calc.formData.printQuantity}</td>
                    <td className="p-2 text-right font-medium">{formatNumber(calc.results.sellingPriceUnit, 2)} ₽</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonSidebar;